import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { MITRE_COLORS_PRIORITY_RANGE } from '@investigator/shared/constants';
import { DetectionCountsByMitreTactic } from '@investigator/features/entities/types';

/**
 * MITRE ATT&CK Tactic names as returned by the API, in kill chain order
 * These are displayed in the footprint visualization
 */
export const MITRE_FOOTPRINT_TACTICS = [
  'Initial Access',
  'Execution',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Command and Control',
  'Exfiltration',
] as const;

export type MitreTactic = (typeof MITRE_FOOTPRINT_TACTICS)[number];

/**
 * Maps MITRE tactic names to color priority levels
 * Priority levels correspond to indices in MITRE_COLORS_PRIORITY_RANGE
 */
export const MITRE_TACTIC_TO_PRIORITY_LEVEL: Record<MitreTactic, number> = {
  'Initial Access': 2,
  Execution: 3,
  'Defense Evasion': 6,
  'Credential Access': 7,
  Discovery: 8,
  'Lateral Movement': 9,
  'Command and Control': 11,
  Exfiltration: 12,
};

export interface MitreTacticsFootprintProps {
  /**
   * Array of MITRE tactic counts from the API
   * Format: [{ mitre_tactic: "Initial Access", detection_count: 5 }, ...]
   */
  mitreTacticCounts: DetectionCountsByMitreTactic[];
}

export const MitreSquare = ({ hexColor }: { hexColor: string }) => {
  return (
    <Box
      backgroundColor={hexColor}
      width="10px"
      height="10px"
      data-testid="mitre-square"
      borderRadius="1px"
    />
  );
};

export const MitreTacticsFootprint = ({
  mitreTacticCounts,
}: MitreTacticsFootprintProps) => {
  const emptySquareColor = useColorModeValue('gray.200', 'gray.600');

  // Convert array to lookup map for O(1) access
  const tacticCountMap = new Map(
    mitreTacticCounts
      .filter((tactic: DetectionCountsByMitreTactic) =>
        MITRE_FOOTPRINT_TACTICS.includes(tactic.mitre_tactic as MitreTactic),
      )
      .map((tactic: DetectionCountsByMitreTactic) => [
        tactic.mitre_tactic,
        tactic.detection_count,
      ]),
  );

  return (
    <Flex gap={1} flexWrap="wrap" p={1} justifyContent="flex-start">
      {MITRE_FOOTPRINT_TACTICS.map((tactic: MitreTactic) => {
        const count = tacticCountMap.get(tactic);
        // Show colored square if count > 0, otherwise empty
        return count && count > 0 ? (
          <MitreSquare
            key={tactic}
            hexColor={
              MITRE_COLORS_PRIORITY_RANGE[
                MITRE_TACTIC_TO_PRIORITY_LEVEL[tactic]
              ]
            }
          />
        ) : (
          <Box
            key={tactic}
            backgroundColor={emptySquareColor}
            width="10px"
            height="10px"
            data-testid="empty-square"
            borderRadius="1px"
          />
        );
      })}
    </Flex>
  );
};
